/**
 * kp-back-to-top — a floating "up" that also says how far down you are.
 *
 * - Progress: the notched outline is drawn to scrollY / scrollable height.
 * - Smart hide: shown after THRESHOLD px; hides while the reader scrolls
 *   down (they are reading, not leaving), comes back the moment they scroll
 *   up. Never shown at the top.
 * - Click: smooth to top, or an instant jump under reduced motion. Uses
 *   Lenis when kp-lenis.js has put one on window, so the two scrollers
 *   never fight.
 */
class KpBackToTop extends HTMLElement {
  static THRESHOLD = 400;
  static JITTER = 5;

  connectedCallback() {
    this.button = this.querySelector('.kp-btt');
    this.progress = this.querySelector('.kp-btt__progress');
    if (!this.button) return;

    this.lastY = window.scrollY;
    this.ticking = false;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onScroll = () => this.schedule();
    this.onClick = () => this.toTop();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.button.addEventListener('click', this.onClick);

    this.update(0);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.button?.removeEventListener('click', this.onClick);
  }

  schedule() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      this.update(y - this.lastY);
      this.lastY = y;
      this.ticking = false;
    });
  }

  update(delta) {
    const y = window.scrollY;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const ratio = max > 0 ? Math.min(1, Math.max(0, y / max)) : 0;
    if (this.progress) this.progress.style.strokeDashoffset = String(1 - ratio);

    if (y < KpBackToTop.THRESHOLD) {
        this.show(false);
      return;
    }

    if (delta > KpBackToTop.JITTER) {
      // Reading downward: get out of the way. Only a scroll up brings it
      // back — a pause used to, and an object surfacing while the reader is
      // still is motion nobody asked for (owner's call, shared with the
      // sticky add to cart).
      this.show(false);
    } else if (delta < -KpBackToTop.JITTER) {
      this.show(true);
    }
  }

  show(on) {
    this.button.classList.toggle('kp-btt--in', on);
  }

  toTop() {
    const instant = this.reduced.matches;
    if (window.__lenis && !instant) {
      window.__lenis.scrollTo(0);
    } else {
      window.scrollTo({ top: 0, behavior: instant ? 'auto' : 'smooth' });
    }
  }
}

if (!customElements.get('kp-back-to-top')) {
  customElements.define('kp-back-to-top', KpBackToTop);
}
